import { useEffect, useRef, useState } from 'react';
import { oneLine } from 'common-tags';

import { ChevronDown } from '@/icons';
import { Link } from '@/atoms';

import type { FunctionComponent, MouseEvent } from 'react';
import type { TypeViewMoreProps } from './types';

// TODO Añadir mutation observable para cuando cambie el tamaño ocultar el botón o añadirlo
// @see https://blog.logrocket.com/guide-to-custom-react-hooks-with-mutationobserver/
// TODO Animar la entrada del linear gradient para que no se aplique de sopeton
// TODO Hacer que el max-height cuando crezca sea dinámico no de 3000px
/**
 * ViewMore
 */
export const ViewMore: FunctionComponent<TypeViewMoreProps> = ({
  className = '',
  style = 'normal',
  icon = false,
  open = false,
  onlySmallDevices = false,
  labels = {
    viewLess: 'Ver menos',
    viewMore: 'Ver más',
  },
  maxHeight = 400,
  children,
}: TypeViewMoreProps) => {
  const [isOpen, setOpen] = useState(open);
  const [isAlwaysOpen, setAlwaysOpen] = useState(false);
  const toggle = (state: boolean) => setOpen(state);
  const viewMore = useRef(null);
  const content = useRef(null);

  useEffect(() => {
    const open = () => {
      setOpen(true);
      setAlwaysOpen(true);
    };

    const close = () => {
      setOpen(false);
      setAlwaysOpen(false);
    };

    // TODO check types
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const noMinimumContent = () => content.current?.clientHeight < maxHeight;

    if (onlySmallDevices) {
      const updateWindowDimensions = () => {
        const largeWidth = window.innerWidth > 640;

        if (noMinimumContent() || (onlySmallDevices && largeWidth)) open();
        else close();
      };

      updateWindowDimensions();

      return () => window.removeEventListener('resize', updateWindowDimensions);
    } else {
      if (noMinimumContent()) open();
    }
  }, [maxHeight, onlySmallDevices]);

  return (
    <div className={`flex flex-col ${className}`} ref={viewMore}>
      <div
        className={oneLine`relative max-h-[3000px] overflow-hidden transition-all ${
          isOpen
            ? ''
            : `after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[150px] after:bg-gradient-to-b after:from-[#f8f7f64d] after:to-[#f7f6f5e6] after:content-['']`
        }
          ${style === 'link' ? 'after:content-none' : ''}
        `}
        style={{ maxHeight: isOpen ? 'none' : maxHeight }}
        ref={content}
      >
        {typeof children === 'function' ? <>{children(isOpen)}</> : children}
      </div>

      <span
        className={oneLine`flex max-w-max cursor-pointer flex-row items-center gap-1 pt-2 ${
          style === 'link'
            ? 'm-0 text-secondary-500 hover:text-secondary-600'
            : 'mx-auto'
        }
        ${isAlwaysOpen ? `hidden` : ''} 
        `}
        onClick={() => toggle(!isOpen)}
      >
        <Link
          as="button"
          type={style === 'link' ? 'secondary' : 'primary'}
          size={style === 'link' ? 'body' : 'small'}
          onClick={(e: MouseEvent<HTMLButtonElement>) => e.preventDefault()}
        >
          {isOpen ? labels.viewLess : labels.viewMore}
        </Link>

        {icon && (
          <ChevronDown
            width={24}
            height={24}
            className={`${isOpen ? '-rotate-180' : ''}`}
          />
        )}
      </span>
    </div>
  );
};
